// Wraps a `ParametricCabinetClass` document into the legacy
// `CabinetClass` interface — same method signatures consumers were
// calling before the refactor (mesh / parts / drillPattern / clamp).
//
// The methods don't know about per-cabinet overrides; they evaluate
// each call at the bare cabinet dims. Code paths that need overrides
// applied (PartEditor, validation per-cabinet) call `evaluateClass`
// directly.

import type { CabinetClass } from "..";
import type { Dims } from "..";
import type { ParametricCabinetClass } from "@/lib/types";
import { legacyMesh, legacyParts, legacyDrillPattern } from "./evaluate";

function clampToBounds(d: Dims, b: CabinetClass["bounds"]): Dims {
  return {
    w: Math.max(b.w[0], Math.min(b.w[1], d.w)),
    h: Math.max(b.h[0], Math.min(b.h[1], d.h)),
    d: Math.max(b.d[0], Math.min(b.d[1], d.d)),
  };
}

export function fromParametric(klass: ParametricCabinetClass): CabinetClass {
  return {
    id: klass.id,
    label: klass.label,
    category: klass.category === "custom" ? "base" : klass.category,
    anchor: klass.anchor,
    defaults: klass.defaults,
    bounds: klass.bounds,
    clamp(d: Dims): Dims { return clampToBounds(d, this.bounds); },
    mesh(d) { return legacyMesh(klass, d); },
    parts(d) { return legacyParts(klass, d); },
    drillPattern(d) { return legacyDrillPattern(klass, d); },
  };
}
